/* eslint-disable max-len */
/* eslint-disable no-shadow */
import { debounce } from "throttle-debounce";
// import { scaleLinear } from 'd3-scale';
import { descending, loadScript, getPositionRelativeToParent } from "../../../utils";
import { MapPopup } from "./map-popup";
// import { convertToGeoJSON } from "./mapbox_helpers";
import { Dispatcher } from "./dispatcher";
import "./scss/_map__base.scss";
import "./scss/_map__popup.scss";
import "./scss/_map__tooltips.scss";
import "./scss/_map__circle_markers.scss";

const DEBUG = true;
const DEFAULT_COLOR = "#585959";

let mapboxgl;

const template = `
  <div class="tsg-map">
    <div class="tsg-map__map"></div>
    <div class="tsg-map__controls"></div>
    <div class="tsg-map__legend"></div>
    <div class="tsg-map__popup-container"></div>
  </div>
`;

const stylePastel =
	"https://api.maptiler.com/maps/d9431abd-4f0b-4805-9179-f250aca98a37/style.json?key=xyX8aavHilZMC9EUtX3J";
const styleSimple = "https://interaktiv.tagesspiegel.de/arenahousing/simpletiles/style.json";
const styleBW =
	"https://api.maptiler.com/maps/8813b5c3-1723-4a23-9aed-180e1c1f99dc/?key=xyX8aavHilZMC9EUtX3J#10.6/52.5003/13.4044";
const styleWahl =
	"https://api.maptiler.com/maps/8813b5c3-1723-4a23-9aed-180e1c1f99dc/style.json?key=xyX8aavHilZMC9EUtX3J";
const styleWahlDe =
	"https://api.maptiler.com/maps/fc6dfb85-835a-4da1-b164-b1b8342baed2/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMap = "https://api.maptiler.com/maps/8067f98c-37ab-4c2d-9db1-105d3eed7cee/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapV2 =
	"https://api.maptiler.com/maps/61c65d25-adda-46e2-83a9-c86153203ea0/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapV3 =
	"https://api.maptiler.com/maps/5705e730-52d1-43b8-b1af-68fc82104f13/style.json?key=xyX8aavHilZMC9EUtX3J";
const warMapDev =
	"https://api.maptiler.com/maps/d0b8a817-007b-47d9-858d-6b06eefd3253/style.json?key=Q08s7QLW69RVwWAg9AaA";
const pipelineMap =
	"https://api.maptiler.com/maps/5c73a149-2362-485b-9dd0-bca543f868f3/style.json?key=xyX8aavHilZMC9EUtX3J";
const styleDark =
	"https://api.maptiler.com/maps/770c4ecc-3780-403a-a72a-1229319a954d/style.json?key=xyX8aavHilZMC9EUtX3J";

class Map extends Dispatcher {
	constructor(container, options) {
		super();
		mapboxgl = window.mapboxgl;
		this.container = container;
		this.options = options;
		this.choropleths = {};
		this.lines = {};
		this.labels = {};
		this.setupHTML();

		// Only load map when close to viewport (to save $$$$ on tiles);
		this.activated = new Promise((resolve) => {
			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].intersectionRatio > 0) {
						this.setupMap();
						observer.unobserve(this.elems.main);
						resolve();
					}
				},
				{ threshold: [0], rootMargin: "600px" }
			);
			observer.observe(this.elems.main);
		});

		// check whether the device is a touch device
		const isTouchDevice = () => {
			return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
		};
		this.isTouch = isTouchDevice();
		this.elems.main.classList.toggle("tsg-map--is-touch", this.isTouch);

		if (this.options.controls) this.setupControls();
		if (this.options.popup) this.setupPopup();
		this.setupResize();
	}

	getStyle(style) {
		if (!style || style === "default" || style === "pastel") {
			this.provider = "maptiler";
			return stylePastel;
		}
		if (style === "simple") {
			this.provider = "self";
			return styleSimple;
		}
		if (style === "bw") {
			this.provider = "self";
			return styleBW;
		}
		if (style === "wahl") {
			this.provider = "maptiler";
			return styleWahl;
		}
		if (style === "wahl_de") {
			this.provider = "maptiler";
			return styleWahlDe;
		}
		if (style === "warMap") {
			this.provider = "maptiler";
			return warMap;
		}
		if (style === "warMapV2") {
			this.provider = "maptiler";
			return warMapV2;
		}
		if (style === "warMapV3") {
			this.provider = "maptiler";
			return warMapV3;
		}
		if (style === "warMapDev") {
			this.provider = "maptiler";
			return warMapDev;
		}
		if (style === "pipelineMap") {
			this.provider = "maptiler";
			return pipelineMap;
		}
		if (style === "dark") {
			this.provider = "maptiler";
			return styleDark;
		}
		/*
    if (style === 'mb') {
      this.provider = 'mapbox';
      mapboxgl.accessToken = mbAccessToken;
      return mbStyle;
    }
    */
		return style;
	}

	setupHTML() {
		this.container.innerHTML = template;
		this.elems = {
			main: this.container.querySelector(".tsg-map"),
			map: this.container.querySelector(".tsg-map__map"),
			legend: this.container.querySelector(".tsg-map__legend"),
			controls: this.container.querySelector(".tsg-map__controls"),
			popupContainer: this.container.querySelector(".tsg-map__popup-container"),
		};
	}

	setupResize() {
		const r = () => {
			this.width = this.elems.main.getBoundingClientRect().width;

			if (this.options.controlsBreakpointMin) {
				this.elems.main.classList.toggle("tsg-map--hide-controls", this.width < this.options.controlsBreakpointMin);
			}

			if (this.options.controlsBreakpointMax) {
				this.elems.main.classList.toggle("tsg-map--hide-controls", this.width > this.options.controlsBreakpointMax);
			}

			if (this.options.largeCircleMarkersBreakpoint) {
				this.elems.main.classList.toggle(
					"tsg-map--large-circle-markers",
					this.width >= this.options.largeCircleMarkersBreakpoint
				);
			}

			if (this.options.style === "simple") {
				this.elems.main.classList.add("tsg-map--no-attribution");
			} else {
				this.elems.main.classList.remove("tsg-map--no-attribution");
			}
			if (this.width <= 640) {
				this.elems.main.parentNode.parentNode.classList.add("tsg-map-mobile");
			} else {
				this.elems.main.parentNode.parentNode.classList.remove("tsg-map-mobile");
			}

			if (this.popup && this.popup.isMobile) {
				this.elems.main.classList.add("tsg-map--popup-mobile");
			} else {
				this.elems.main.classList.remove("tsg-map--popup-mobile");
			}

			this.dispatch("resize");
		};

		r();

		let ww = window.innerWidth;
		window.addEventListener(
			"resize",
			debounce(200, () => {
				if (ww !== window.innerWidth) {
					ww = window.innerWidth;
					r();
				}
			})
		);
	}

	setupMap() {
		// create map
		this.map = new mapboxgl.Map({
			container: this.elems.map,
			style: this.options.styleJson || this.getStyle(this.options.style),
			minZoom: this.options.minZoom,
			maxZoom: this.options.maxZoom,
			maxBounds: this.options.maxBounds,
			attributionControl: false,
			pitchWithRotate: false,
			dragRotate: false,
			touchZoomRotate: false,
			touchPitch: false,
		});
		this.map.touchZoomRotate.disableRotation();
		this.attributionControl = new mapboxgl.AttributionControl({
			compact: false,
		});
		this.map.addControl(this.attributionControl);

		if (DEBUG) window.map = this;

		// this.map.on('load', function () {
		//   this.map.resize();
		// });

		// store a loading promise that can be used whenver the stylesheet
		// needs to be loaded already (e.g. when one needs access to the layers)
		this.loaded = new Promise((resolve) => {
			this.map.once("styledata", () => {
				resolve();
			});
		});

		// store the ids of label layers (for making it possible to hide them)
		this.loaded.then(() => {
			this.labelLayerIds = this.map
				.getStyle()
				.layers.filter((x) => x.type === "symbol" && x.layout["text-field"])
				.filter((x) => x.visibility !== "none") // attention: we only save visible layers
				.map((x) => x.id);
			this.firstLabelLayer = this.labelLayerIds[0];
			if (this.getStyle(this.options.style) === stylePastel && !this.options.styleJson) {
				this.firstLabelLayer = "airport-label";
			} // HOTFIX
			this.symbolLayerIds = this.map
				.getStyle()
				.layers.filter((x) => x.type === "symbol")
				.map((x) => x.id);
		});

		// patch fonts
		if (this.options.useFranklin) {
			this.loaded.then(() => {
				this.map.style.glyphManager.url = "https://interaktiv.tagesspiegel.de/maptiles/fonts/{fontstack}/{range}.pbf";
				this.symbolLayerIds.forEach((lid) => {
					const lp = this.map.getLayoutProperty(lid, "text-font");
					const tf = lp ? lp[0] : "";
					const italic = tf.indexOf("Italic") !== -1 ? " Italic" : "";
					const medium = tf.indexOf("Medium") !== -1;
					const demi = tf.indexOf("Demi") !== -1;
					const bold = tf.indexOf("Bold") !== -1;
					const weight = medium ? "Medium" : demi ? "Demi" : bold ? "Demi" : "Book";
					const font = `Franklin Gothic ${weight}${italic}`;

					this.map.setLayoutProperty(lid, "text-font", [font]);
				});
			});
		}

		if (!this.options.labels) this.hideLabels();
		if (this.options.hideBackgroundMap) this.hideBackgroundMap();

		// change the language of the map's labels
		// this.setLanguage(getLanguage());

		// make the map noninteracive (we never want interactivity in the inital state
		this.setInteractivity(false);

		// but activate interactivity for certain interactions

		if (!this.options.noAutoInteractiveClick) {
			this.map.on("click", () => {
				this.setInteractivity(true);
			});
			this.map.on("mousedown", () => {
				this.setInteractivity(true);
			});
		}
		if (!this.options.noAutoInteractiveTouch) {
			this.map.on("touchstart", (e) => {
				if (!this.interactive && e.originalEvent.targetTouches.length === 2) {
					this.setInteractivity(true);
				}
			});
		}

		// go to the inital view as set in the options
		this.toInitialView(true, false);

		// keep view on resize, unless user has interacted already (don't annoy them)
		this.map.on("resize", () => {
			if (!this.userInteracted) this.toInitialView();
		});

		// detect user interaction
		"dragstart rotatestart boxzoomstart wheel".split(" ").forEach((e) => {
			this.map.once(e, () => {
				this.userInteracted = true;
			});
		});

		// track custom layers
		this.customLayers = [];

		// hide "duplicate layer id" errors - these happen very often
		// with maptiler styles and clutter the console
		this.map.on("error", (e) => {
			if (e.error.message.indexOf("duplicate layer") !== -1) return;
			console.error(e.error);
		});
	}

	toInitialView(noAnimation, noPadding) {
		this.programmaticMove = true;

		const cam = this.map.cameraForBounds(this.options.initialBounds);
		if (cam) {
			if (this.options.initialMinZoom && cam.zoom < this.options.initialMinZoom) {
				cam.zoom = this.options.initialMinZoom;
				if (this.options.initialCenter) {
					cam.center = this.options.initialCenter;
				}
			}
			if (noPadding) {
				cam.padding = {
					left: 0,
					top: 0,
					bottom: 0,
					right: 0,
				};
			} else {
				cam.padding = this.options.padding;
			}
			cam.animate = !noAnimation;
			this.map.easeTo(cam);
			this._blockMoved = true;
			this.elems.main.classList.remove("tsg-map--moved");
		}
	}

	setInteractivity(interactive) {
		this.interactive = interactive;
		this.elems.main.classList.toggle("tsg-map--interactive", interactive);

		// these types of handlers are always disabled
		this.map.keyboard.disable();
		this.map.boxZoom.disable();

		// these will be enabled or disabled
		const handlers = [
			"scrollZoom",
			"dragPan",
			// 'dragRotate',
			"doubleClickZoom",
			"touchZoomRotate",
			// 'touchPitch',
		];

		handlers.forEach((h) => {
			this.map[h][interactive ? "enable" : "disable"]();
		});

		if (interactive) this.userInteracted = true;
	}

	hideLabels() {
		this.loaded.then(() => {
			this.labelLayerIds.forEach((l) => {
				this.map.setLayoutProperty(l, "visibility", "none");
			});
		});
	}

	showLabels() {
		this.loaded.then(() => {
			this.labelLayerIds.forEach((l) => {
				this.map.setLayoutProperty(l, "visibility", "visible");
			});
		});
	}

	hideBackgroundMap() {
		this.loaded.then(() => {
			const layers = this.map.getStyle().layers;

			layers.forEach((l) => {
				if (this.customLayers.indexOf(l.id) === -1) {
					this.map.setLayoutProperty(l.id, "visibility", "none");
				}
			});
		});
	}

	showBackgroundMap() {
		this.loaded.then(() => {
			const layers = this.map.getStyle().layers;

			layers.forEach((l) => {
				this.map.setLayoutProperty(l.id, "visibility", "visible");
			});
		});
	}

	hideTooltip() {
		this.elems.main.classList.add("tsg-map--hide-tooltip");
	}

	showTooltip() {
		this.elems.main.classList.remove("tsg-map--hide-tooltip");
	}

	setLanguage(l) {
		if (this.provider === "self") return;
		if (this.provider === "maptiler") this.map.setLanguage(l, true);
		if (this.provider === "mapbox") {
			(this.languagePlugin
				? Promise.resolve()
				: loadScript(`${PATH}vendor/mapbox-gl-language.js`).then(() => {
						this.languagePlugin = new window.MapboxLanguage({});
						this.map.addControl(this.languagePlugin);
				  })
			).then(() => {
				this.loaded.then(() => {
					this.map.setStyle(this.languagePlugin.setLanguage(this.map.getStyle(), l));
				});
			});
		}
	}

	setupPopup() {
		const opts = {
			variant: this.options.popupVariant,
			fixedBreak: this.options.popupFixedBreak,
			barMessage: this.options.popupBarMessage,
			barMessageClose: this.options.popupBarMessageClose,
			callbackClose: (isFixed) => {
				this.elems.main.classList.remove("tsg-map--popup-open");
				this.elems.main.classList.remove("tsg-map--popup-open-mobile");
				if (!isFixed && this.options.popupShift) {
					this.map.easeTo({
						padding: {
							left: 0,
							top: 0,
							right: 0,
							bottom: 0,
						},
					});
				}
				if (this.options.popupCallbackClose) {
					this.options.popupCallbackClose(this);
				}
			},
			callbackOpen: (fixed) => {
				this.elems.main.classList.add("tsg-map--popup-open");
				if (this.popup.isMobile) {
					this.elems.main.classList.add("tsg-map--popup-open-mobile");
				} else {
					this.elems.main.classList.remove("tsg-map--popup-open-mobile");
				}
				if (this.options.popupShift) {
					this.activated.then(() => {
						if (fixed) {
							this.map.setPadding(this.popup.getMapPadding());
							this.toInitialView();
						} else {
							this.map.easeTo({ padding: this.popup.getMapPadding() });
						}
					});
				}
				if (this.options.popupCallbackOpen) {
					this.options.popupCallbackOpen(this);
				}
			},
		};
		if (this.options.popupWidth) opts.width = this.options.popupWidth;
		this.popup = new MapPopup(this.elems.popupContainer, opts);
	}

	setupControls() {
		if (this.options.scale) {
			this.activated.then(() => {
				this.map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
			});
		}

		if (this.options.controlsLeft) this.elems.controls.classList.add("tsg-map__controls--left");
		if (this.options.controlsVertical) this.elems.controls.classList.add("tsg-map__controls--vertical");

		const addControl = (type, handler, text) => {
			const e = document.createElement("div");
			e.classList.add("tsg-map-control");
			e.classList.add(`tsg-map-control--${type}`);
			e.classList.add("tsg-font-sans");
			e.addEventListener("click", handler);
			if (text) {
				e.innerHTML = `<div class="tsg-map-control__texth">${text}</div><div class="tsg-map-control__text">${text}</div>`;
			}
			this.elems.controls.appendChild(e);
			return e;
		};

		this.controlZoomOut = addControl("zoom-out", () => {
			this._blockMoved = true;
			this.map.zoomTo(this.map.getZoom() - 0.7);
			this.setInteractivity(true);
		});
		this.controlZoomIn = addControl("zoom-in", () => {
			this._blockMoved = true;
			this.map.zoomTo(this.map.getZoom() + 0.7);
			this.setInteractivity(true);
		});
		this.controlReset = addControl("reset", () => {
			this.toInitialView();
		});

		this.activated.then(() => {
			this.map.on("zoomend", () => {
				if (!this._blockMoved) this.elems.main.classList.add("tsg-map--moved");
			});
			this.map.on("moveend", () => {
				if (!this._blockMoved) this.elems.main.classList.add("tsg-map--moved");
				this._blockMoved = false;
			});
		});

		this.controlActivate = addControl(
			"activate",
			() => {
				this.setInteractivity(true);
				this.map.zoomTo(this.map.getZoom() + 0.1);
			},
			"Zoomen aktivieren"
		);
		this.controlDeactivate = addControl(
			"deactivate",
			() => {
				this.setInteractivity(false);
				this.map.zoomTo(this.map.getZoom() - 0.1);
			},
			"Scrollen aktivieren"
		);
	}

	hideControls() {
		this.elems.main.classList.add("tsg-map--hide-controls");
	}

	showControls() {
		this.elems.main.classList.remove("tsg-map--hide-controls");
	}

	hideAttribution() {
		this.elems.main.classList.add("tsg-map--no-attribution");
		const attrib = document.querySelector(".mapboxgl-ctrl-attrib-inner");
		if (attrib) attrib.style.display = "none";
	}

	showAttribution() {
		this.elems.main.classList.remove("tsg-map--no-attribution");
		const attrib = document.querySelector(".mapboxgl-ctrl-attrib-inner");
		if (attrib) attrib.style.display = "block";
	}

	onZoom(zoomCallback) {
		this.activated.then(() => {
			zoomCallback();
			this.map.on("zoom", () => {
				zoomCallback();
			});
		});
	}

	updateChoroplethOpts(choroplethId, opts) {
		this.choropleths[choroplethId].options = { ...this.choropleths[choroplethId].options, ...opts };
	}

	generateProperty(original, selected, hovering, filtered) {
		let property = original;

		const clickCondition = ["boolean", ["feature-state", "click"], false];
		const hoverCondition = ["boolean", ["feature-state", "hover"], false];
		const filterCondition = ["boolean", ["get", "filtered"], true];

		if (selected || hovering || filtered) {
			property = ["case"];

			if (selected) {
				property.push(clickCondition, selected);
			}
			if (hovering) {
				property.push(hoverCondition, hovering);
			}
			if (filtered) {
				property.push(filterCondition, filtered);
			}
			property.push(original);
		}

		return property;
	}

	zoomToFeature(feature, zoom = null, pad = null) {
		const coordinates = Array.isArray(feature.geometry.coordinates[0])
			? feature.geometry.coordinates[0]
			: feature.geometry.coordinates;

		const bounds = new mapboxgl.LngLatBounds();

		coordinates.forEach((coord) => {
			const tempCoord = new mapboxgl.LngLat(coord[0], coord[1]);
			bounds.extend(tempCoord);
		});

		this.programmaticMove = true;
		const options = zoom ? { zoom } : {};

		options.essential = true;
		options.duration = 1000;

		if (pad) {
			options.padding = pad;
		}

		this.map.fitBounds(bounds, options);
	}

	setPaintProperties({ id, type, paintProperties }) {
		let prefix;
		if (type === "point") {
			prefix = "circle";
		} else if (type === "line") {
			prefix = "line";
		} else {
			prefix = "fill";
		}

		Object.entries(paintProperties).forEach(([paintProp, value]) => {
			this.map.setPaintProperty(id, `${prefix}-${paintProp}`, value);
		});
	}

	setLayerFilter({ id, filter }) {
		this.map.setFilter(id, filter);
	}

	// 	if (colorKey && colorStops) {
	// 		this.map.setPaintProperty(
	// 			id,
	// 			propName,
	// 			{
	// 				property: colorKey,
	// 				stops: colorStops
	// 			}
	// 		)
	// 	} else {
	// 		this.map.setPaintProperty(
	// 			id,
	// 			propName,
	// 			defaultColor || DEFAULT_COLOR
	// 		)
	// 	}
	// }

	createLabelsGeo(labels, offsetv, iconImage) {
		// Create a geojson for the labels
		const geojson = {
			type: "FeatureCollection",
			features: [],
		};

		labels.forEach((label) => {
			let anchor = "center";
			let offset = [0, 0];

			if (label.position === "bottom" || label.icon || iconImage) {
				anchor = "top";
				offset = [0, offsetv * 1.2];
			}

			if (label.position === "top") {
				anchor = "bottom";
				offset = [0, -offsetv * 0.8];
			}

			if (label.position === "left") {
				anchor = "right";
				offset = [-offsetv, 0.1];
			}

			if (label.position === "right") {
				anchor = "left";
				offset = [offsetv, 0.1];
			}

			const feature = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: label.lnglat,
				},
				properties: {
					text: label.name,
					icon: label.icon || iconImage || "",
					opacity: label.opacity === "undefined" ? 1 : label.opacity,
					anchor,
					offset,
				},
			};
			geojson.features.push(feature);
		});

		return geojson;
	}

	addPointLayer(opts) {
		const config = {
			id: opts.id,
			type: "circle",
			source: opts.id,
			layout: {},
			paint: {
				"circle-radius": 4,
				"circle-color": DEFAULT_COLOR,
				"circle-stroke-color": "#fff",
				"circle-stroke-width": ["case", ["boolean", ["feature-state", "hover"], false], 3, 0.5],
			},
		};

		if (opts.tileSource) {
			config["source-layer"] = opts.id;
		}
		if (opts.minzoom) {
			config.minzoom = opts.minzoom;
		}
		if (opts.maxzoom) {
			config.maxzoom = opts.maxzoom;
		}

		this.map.addLayer(config, opts.beforeLayer);
	}

	addLineLayer(opts) {
		const config = {
			id: opts.id,
			type: "line",
			source: opts.id,
			layout: {},
			paint: {
				"line-color": DEFAULT_COLOR,
				"line-width": ["case", ["boolean", ["feature-state", "hover"], false], 6, 2],
			},
		};

		if (opts.tileSource) {
			config["source-layer"] = opts.id;
		}

		this.map.addLayer(config, opts.beforeLayer);
	}

	addPolygonLayer(opts) {
		const configCommon = {
			source: opts.id,
		};

		if (opts.minzoom) {
			configCommon.minzoom = opts.minzoom;
		}
		if (opts.maxzoom) {
			configCommon.maxzoom = opts.maxzoom;
		}

		if (opts.tileSource) {
			configCommon["source-layer"] = opts.id;
		}

		this.map.addLayer(
			{
				...configCommon,
				id: opts.id,
				type: "fill",
				paint: {
					"fill-opacity": 0.65,
				},
			},
			opts.beforeLayer
		);

		this.map.addLayer({
			...configCommon,
			id: `${opts.id}-hover`,
			type: "line",
			paint: {
				"line-color": "#fff",
				// 'line-color': [
				// 	'case',
				// 	['boolean', ['feature-state', 'hover'], false],
				// 	"#000",
				// 	"#fff"
				// ],
				"line-width": ["case", ["boolean", ["feature-state", "hover"], false], 3, 0],
			},
		});
	}

	addGeometry(dataLink, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				if (!this.map.getSource(opts.id)) {
					if (opts.tileSource) {
						this.map.addSource(opts.id, {
							type: "vector",
							url: opts.tileSource,
							generateId: true,
						});
					} else {
						this.map.addSource(opts.id, {
							type: "geojson",
							data: dataLink,
							generateId: true,
						});
					}

					// this.map.addSource(opts.id, {
					// 	type: "vector",
					// 	tiles: [
					// 	 "https://api.maptiler.com/tiles/eacbfba5-760e-4fc1-bb6a-85c46b5219a4/{z}/{x}/{y}.pbf?key=j9idjzvHhlVjGJo5rw8Z"
					// 	],
					// 	generateId: true
					// });
				}
				if (this.map.getLayer(opts.id)) {
					this.map.removeLayer(opts.id);
				}

				if (opts.type === "point") {
					this.addPointLayer(opts);
				} else if (opts.type === "line") {
					this.addLineLayer(opts);
				} else {
					this.addPolygonLayer(opts);
				}

				if (opts.paintProperties) {
					this.setPaintProperties(opts);
				}

				const mousemove = (e, a, b) => {
					e.originalEvent.stopPropagation();
					if (e.features.length > 0) {
						this.map.getCanvas().style.cursor = "pointer";
						const feature = e.features[0];
						opts.mousemoveCallback(opts.id, feature);
					}
				};

				const mouseleave = (e, a, b) => {
					e.originalEvent.stopPropagation();
					this.map.getCanvas().style.cursor = "";
					opts.mouseleaveCallback(opts.id);
				};

				const click = (e) => {
					e.preventDefault();
					e.originalEvent.stopPropagation();
					if (e.features.length > 0) {
						const feature = e.features[0];
						const zoom = Math.max(this.map.getZoom(), opts.clickMinZoom);
						opts.clickCallback(opts.id, feature);

						this.zoomToFeature(feature, zoom, opts.zoomPadding || this.options.padding);
					}
				};

				const backgroundClick = (e) => {
					if (!e._defaultPrevented) {
						opts.backgroundClickCallback(opts.id);
					}
				};

				this.map.on("mousemove", opts.id, mousemove);
				this.map.on("mouseleave", opts.id, mouseleave);
				this.map.on("click", opts.id, click);
				this.map.on("click", backgroundClick);
			});
	}

	highlightFeature(layer, feature, existingHighlight, highlightIsSelected) {
		if (!feature?.id || existingHighlight?.id === feature.id) {
			return;
		}

		if (!highlightIsSelected) {
			this.unhighlightFeature(layer, existingHighlight);
		}

		this.map.setFeatureState({ source: layer, id: feature.id }, { hover: true });
	}

	unhighlightFeature(layer, feature) {
		if (!!feature?.id) {
			this.map.setFeatureState({ source: layer, ...{}, id: feature.id }, { hover: false });
		}
	}

	addRasterLayer(dataLink, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				this.map.addSource(opts.id, {
					type: "raster",
					url: dataLink,
					// tileSize': 256,
				});

				if (this.map.getLayer(opts.id)) {
					this.map.removeLayer(opts.id);
				}
				this.map.addLayer({
					id: opts.id,
					type: "raster",
					source: opts.id,
					// minzoom: 10,
					// maxzoom: 16
				});
			});
	}

	getFeatureFromLatLon(lat, lon) {
		const point = this.map.project([lon, lat]);
		const features = this.map.queryRenderedFeatures(point, {
			layers: [this.pointOptions.id],
		});
		return features;
	}

	hideLayer(geometryId) {
		this.loaded.then(() => {
			if (this.map.getLayer(geometryId)) this.map.setLayoutProperty(geometryId, "visibility", "none");
		});
	}

	showLayer(geometryId) {
		this.loaded.then(() => {
			if (this.map.getLayer(geometryId)) this.map.setLayoutProperty(geometryId, "visibility", "visible");
		});
	}

	addLabels(labels, opts) {
		this.activated
			.then(() => this.loaded)
			.then(() => {
				const options = {
					minZoom: 0,
					offsetv: 0,
					fontSize: 14,
					color: "#000000",
					halo: true,
					haloWidth: 2,
					iconImage: "",
					haloColor: "#ffffff",
					textAllowOverlap: false,
					textAnchor: "center",
					...opts,
				};

				const id = options.id || `custom_labels_${options.minZoom}`;

				this.labels[id] = {
					id,
					labels,
					...options,
				};

				const geojson = this.createLabelsGeo(labels, this.labels[id].offsetv, this.labels[id].iconImage);

				// const style = this.map.getStyle();
				// style.glyphs = 'http://fonts.openmaptiles.org/{fontstack}/{range}.pbf';
				// this.map.setStyle(style);

				this.loaded.then(() => {
					this.map.addSource(id, {
						type: "geojson",
						data: geojson,
					});

					this.map.addLayer({
						id,
						type: "symbol",
						minzoom: this.labels[id].minZoom,
						maxzoom: this.labels[id].maxZoom || 22,
						source: id,
						layout: {
							"text-field": ["get", "text"],
							"text-anchor": ["get", "anchor"],
							"text-offset": ["get", "offset"],
							"text-size": this.labels[id].fontSize,
							"text-font": [
								this.labels[id].font
									? this.labels[id].font
									: this.options.useFranklin
									? "Franklin Gothic Book"
									: "PT Sans Regular",
							],
							"text-letter-spacing": this.labels[id].letterSpacing,
							"icon-image": ["get", "icon"],
							"text-allow-overlap": this.labels[id].textAllowOverlap,
							"text-transform": this.labels[id].textTransform || "none",
							"text-anchor": this.labels[id].textAnchor,
							// 'icon-ignore-placement': true,
						},
						paint: {
							"text-opacity": ["get", "opacity"],
							"text-color": this.labels[id].color,
							"text-halo-color": this.labels[id].haloColor,
							"text-halo-width": this.labels[id].halo ? this.labels[id].haloWidth : 0,
						},
					});
				});
				this.customLayers.push(id);
			});
	}
}

export default Map;
